import {type RefObject, useCallback, useRef, useSyncExternalStore} from 'react'

/**
 * Whether the board has grown past the window, so the page is scrolling instead of it.
 *
 * The board owns its scroll containers, so the page has to give it a height that fits
 * the viewport. Nothing in the API can express that requirement, and getting it wrong
 * produces no error: the board simply stretches the document, and everything that
 * measures against a scroll viewport is wrong from then on. A virtualizer asks its
 * viewport how tall it is, a container that never scrolls answers "as tall as everything
 * in me", every card falls inside the window, and the board renders the lot. That looks
 * like virtualization not working rather than like a height that was never set.
 *
 * One measurement, and it is the failure itself rather than a proxy for it: a board
 * taller than the window has parts nobody can reach without scrolling the page past it,
 * which takes the sticky column headers with it, measures the auto-scroll buffers
 * against a rectangle that is half off-screen, and leaves the paging sentinels at the
 * bottom of the board permanently out of view. Being handed no height at all is the
 * usual way to arrive here, because `block-size: 100%` of a parent with no height of its
 * own resolves to the board's own contents. Being handed a silly one is the other, and
 * it is the same failure.
 *
 * What it does not catch: an unbounded board holding few enough cards to fit the window.
 * That board has the same defect and no symptom yet, and the difference between "my
 * percentage height did not resolve" and "my content is short" is not a question the DOM
 * will answer. This reports the failure once it is real.
 */
export function useHasUnboundedHeight(scrollRef: RefObject<HTMLElement | null>): boolean {
  // The measurement is cached rather than taken in `getSnapshot`, which React calls on
  // every render. Reading `clientHeight` forces layout, and doing that each time the
  // board re-renders would make a diagnostic more expensive than the thing it is
  // diagnosing. The subscription measures, and only wakes React when the answer changes.
  const answerRef = useRef(false)

  const subscribe = useCallback(
    (onChange: () => void) => {
      const element = scrollRef.current

      const check = () => {
        const current = scrollRef.current
        const next = current !== null && current.clientHeight > window.innerHeight
        if (next === answerRef.current) return

        answerRef.current = next
        onChange()
      }

      check()

      const observer = new ResizeObserver(check)
      if (element) observer.observe(element)

      // Half of this comparison is the window, and no ResizeObserver can observe that.
      // It matters in exactly the case being caught: an unbounded board is sized by its
      // contents, so narrowing the window leaves its box untouched and the observer
      // silent, while the board it is measuring has just grown past the viewport.
      // eslint-disable-next-line github/prefer-observers
      window.addEventListener('resize', check)

      return () => {
        observer.disconnect()
        window.removeEventListener('resize', check)
      }
    },
    [scrollRef],
  )

  const getSnapshot = useCallback(() => answerRef.current, [])

  return useSyncExternalStore(subscribe, getSnapshot, getBoundedOnTheServer)
}

/**
 * There is no window on the server, so nothing can be measured against one and the board
 * is never reported. The first client render agrees, because the subscription has not
 * run yet, so hydration matches and the answer arrives immediately afterwards.
 */
function getBoundedOnTheServer(): boolean {
  return false
}
